import './PlanDetailsPage.css';

import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchPlanData } from '../../../services/b2b-plans-apiService';
import { LoginContext } from '../../../App';
import { fullImagePath, generateClassName, returnArrayAsContentField } from '../../b2b-common/helpers';
import Banner from '../../b2b-common/Banner';
import Frame from '../../b2b-common/Frame';
import AccessLevel from '../components/AccessLevel';
import Stage from '../components/Stage';
import AreaMiniCard from '../components/AreaMiniCard';
import ElementsCarousel from '../../b2b-common/ElementsCarousel';
import CustomButton from '../../b2b-common/CustomButton';
import { AddCircleOutline, AutoStories, Edit, MoreVert } from '@mui/icons-material';
import B2BElementsExplorer from '../../b2b-common/B2BElementsExplorer';
import AreaIndicatorCard from '../components/area-indicators/AreaIndicatorCard';
import { B2BContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import MoreButton from '../../b2b-common/MoreButton';
import LogbookModalFactory from '../../b2b-logbook/forms/LogbookModalFactory';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import FieldText from '../../b2b-common/FieldText';
import DatesField from '../components/dates/DatesField';
import PlanSDGsImpactField from '../../b2b-frameworks/components/framework-impact/PlanSDGsImpactField';
import AreaSDGsImpactField from '../../b2b-frameworks/components/framework-impact/AreaSDGsImpactField';
import PlanFullStatus from '../components/plans/PlanFullStatus';
import AreaFullStatus from '../components/areas/AreaFullStatus';

// Local rendering functions

const AchievementsAndAccessStageDetails = ({ FullStatusComponent, startDate, endDate, progress, status, trend, readOnly, docStatus,
    showAccessLevel = false, showStage = false, className = null }) => {
    const { t } = useB2BTranslation('b2b-plans');
    return (
        <div className={generateClassName('plan-details-page-achievements', '', className)}>
            <DatesField
                startDate={startDate}
                endDate={endDate}
                className={'plan-details-page-dates-field'}
            />
            <FullStatusComponent
                status={status} trend={trend} progress={progress}
                startDate={startDate} endDate={endDate}
                className={'plan-details-page-plan-full-status'}
            />
            {showAccessLevel || showStage ? (
            <div className='plan-details-page-achievements-access-level-stage'>
                {showAccessLevel && (
                    <AccessLevel
                        readOnly={readOnly}
                        className={'plan-details-page-access-level'}
                    />
                )}
                {showStage && (
                    <Stage
                        stage={docStatus}
                        className={'plan-details-page-stage'}
                    />
                )}
                </div>
            ) : null}
        </div>
    );
};

export const PlanAchievementsAndAccessStageDetails = ({ currentPlanData }) => {
    return (
        <AchievementsAndAccessStageDetails
            FullStatusComponent={PlanFullStatus}
            startDate={currentPlanData.planStartDate}
            endDate={currentPlanData.planEndDate}
            progress={currentPlanData.snapshot ? currentPlanData.snapshot.progress : 0}
            status={currentPlanData.snapshot ? currentPlanData.snapshot.status : "NO_STATUS"}
            trend={currentPlanData.snapshot ? currentPlanData.snapshot.trend : "NO_TREND"}
            readOnly={currentPlanData.readOnly}
            docStatus={currentPlanData.planStatus}
        />
    );
}

export const AreaAchievementsAndAccessStageDetails = ({ selectedArea }) => {
    return (
        <AchievementsAndAccessStageDetails
            FullStatusComponent={AreaFullStatus}
            startDate={selectedArea.areaStartDate}
            endDate={selectedArea.areaEndDate}
            progress={selectedArea.snapshot ? selectedArea.snapshot.progress : 0}
            status={selectedArea.snapshot ? selectedArea.snapshot.status : "NO_STATUS"}
            trend={selectedArea.snapshot ? selectedArea.snapshot.trend : "NO_TREND"}
            readOnly={selectedArea.readOnly}
            docStatus={selectedArea.areaStatus}
            className={'plan-details-page-area-achievements'}
        />
    );
}

const PlanDetails = ({ currentPlanData, handleEditPlan }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const [showLogbookModal, setShowLogbookModal] = useState(false);

    return (
        <div className='plan-details-page-header'>
            <div className='plan-details-page-header-image'>
                <img src={fullImagePath(currentPlanData.imagePath)} alt={currentPlanData.planName} className='plan-details-page-image' />
                <MoreButton icon={<MoreVert />}
                    rolesAllowed={['ADMIN', 'EDITOR', 'USER']}
                    buttons={[
                        {
                            label: t('Edit'),
                            icon: <Edit />,
                            rolesAllowed: ['ADMIN', 'EDITOR'],
                            onClick: () => handleEditPlan(currentPlanData),
                        },
                        {
                            label: t('Logbook'),
                            icon: <AutoStories />,
                            rolesAllowed: [],
                            b2bProducts: ['B2B_LOGBOOK'],
                            confirm: false,
                            onClick: () => setShowLogbookModal(true)
                        }
                    ]}
                    dropPositionAlign='right'
                    className='plan-details-page-header-more-button jb01-more-button'
                />
            </div>
            <Frame title={t('Plan Details')} className='plan-details-page-frame transparent-no-title-theme'>
                <FieldText
                    label={t('Plan Description')} text={currentPlanData.planDescription} className='plan-details-page-plan-description'
                    labelAlign='left' textAlign='left'
                    // textLinesLimit={6}
                    minLines={6}
                    scrollable={true}
                    heightInLines={12}
                    CRsDetection={true}
                />
                <div className='plan-details-page-status-progress'>
                    <PlanAchievementsAndAccessStageDetails
                        currentPlanData={currentPlanData}
                    />
                    <PlanSDGsImpactField planUUID={currentPlanData.planUUID} className={'plan-details-page-sdgs-impact'} />
                </div>
            </Frame>
            <LogbookModalFactory logbookRelatedEntity={currentPlanData} open={showLogbookModal}
                onClose={() => { setShowLogbookModal(false) }} />
        </div>
    );
}

const AreaDetails = ({ selectedArea }) => {
    const { t } = useB2BTranslation('b2b-plans');
    return (
        <Frame title={t('Area Details')} className='plan-details-page-area-frame transparent-no-title-theme'>
            <FieldText
                label={t('Area Description')} text={selectedArea.areaDescription} className='plan-details-page-area-description'
                labelAlign='left' textAlign='left'
                minLines={6}
                scrollable={true}
                heightInLines={6}
                CRsDetection={true}
            />
            <div className='plan-details-page-area-status-progress'>
                <AreaAchievementsAndAccessStageDetails
                    selectedArea={selectedArea}
                />
                <AreaSDGsImpactField planUUID={selectedArea.plan.planUUID} planAreaId={selectedArea.areaId} className={'plan-details-page-area-sdgs-impact'} />
            </div>
        </Frame>
    );
}

// Main component

const PlanDetailsPage = () => {

    const { t } = useB2BTranslation('b2b-plans');

    const navigate = useNavigate();
    const location = useLocation();
    const { setNotification } = useContext(B2BContentContext);

    const { loginData } = useContext(LoginContext);
    const [error, setError] = useState(null);
    const [currentPlanData, setCurrentPlanData] = useState(null);
    const [selectedArea, setSelectedArea] = useState(null);
    const [selectedAreaIndicatorId, setSelectedAreaIndicatorId] = useState(null);

    const { planUUID } = useParams();
    const [currentPage, setCurrentPage] = useState('/plans/plans-explorer');

    const gettingPlanData = async (selectedAreaId) => {
        // console.log('PlanDetailsPage planUUID:', planUUID);
        const areaId = selectedAreaId ? selectedAreaId : selectedArea ? selectedArea.areaId : null;
        const { accessToken } = loginData;
        if (planUUID) {
            try {
                const planData = await fetchPlanData(accessToken, planUUID, true);
                // console.log('fetchPlanData response:', planData);
                setCurrentPlanData(planData);
                setCurrentPage(`/plans/plans-explorer/${planData.planUUID}`);
                if (areaId) {
                    const area = planData.areaDTOs.find(a => a.areaId === areaId);
                    // console.log('Selected area:', area);
                    area && setSelectedArea(area);
                }
            } catch (error) {
                setError(error.message);
                console.error('Error fetching plan data:', error);
                showNotification(setNotification, 'error', 'Error fetching plan data: ' + error.message, 3000);
            }
        }
    }

    useEffect(() => {
        // console.log('PlanDetailsPage location:', location); 
        const queryParams = new URLSearchParams(location.search);
        // console.log('PlanDetailsPage queryParams:', queryParams); 
        const selectedAreaId = Number(queryParams.get('selectedAreaId'));
        // console.log('PlanDetailsPage selectedAreaId:', selectedAreaId); 
        const selectedAreaIndicatorId = Number(queryParams.get('selectedAreaIndicatorId'));
        // console.log('PlanDetailsPage selectedAreaIndicatorId:', selectedAreaIndicatorId);
        setSelectedAreaIndicatorId(selectedAreaIndicatorId);

        gettingPlanData(selectedAreaId);
    }, [location, planUUID, loginData]);

    const handleEditPlan = (plan) => {
        //console.log('handleEditPlan: ', plan);
        //const from = `/plans/plans-explorer/${plan.planUUID}`;
        navigate(`/plans/plans-explorer/edit`, { state: { plan, from: currentPage } });
    }

    const handleSelectArea = (element) => {
        const area = element.props.area;
        // console.log('Selected area: ', area);
        setSelectedArea(area);
    }

    const handleAddNewArea = () => {
        // console.log('handleAddNewArea');
        navigate(`/plans/plans-explorer/${planUUID}/areaNew`, { state: { plan: currentPlanData, from: currentPage } });
    }

    const handleDeletedArea = (areaId) => {
        // console.log('handleRemoveArea: ', areaId);
        setCurrentPlanData({
            ...currentPlanData,
            areaDTOs: currentPlanData.areaDTOs.filter(area => area.areaId !== areaId)
        });
        setSelectedArea(null);
    }

    const handleAddNewAreaIndicator = () => {
        // console.log('handleAddNewAreaIndicator');
        navigate(`/plans/plans-explorer/${planUUID}/${selectedArea.areaId}/areaIndicatorNew`, { state: { plan: currentPlanData, area: selectedArea, from: currentPage } });
    }

    const handleDeletedAreaIndicator = (areaIndicatorId) => {
        // console.log('handleDeletedAreaIndicator: ', areaIndicatorId);
        setSelectedArea({
            ...selectedArea,
            areaIndicatorDTOs: selectedArea.areaIndicatorDTOs.filter(areaIndicator => areaIndicator.areaIndicatorId !== areaIndicatorId)
        });
        gettingPlanData(selectedArea.areaId);
        setSelectedAreaIndicatorId(null);
    }

    const handleRefreshPlanData = () => {
        // console.log('handleRefreshPlanData');
        gettingPlanData();
    }

    return (
        (currentPlanData === null) ?
            (
                <div className='plan-details-page-container'>
                    <p>Loading plan details [{planUUID}]...</p>
                </div>
            ) :
            (
                <div className='plan-details-page-container'>
                    <Banner message={`${currentPlanData.planName}`} headingLevel='h1' sectionTittle={'Plan'}
                        className={'banner_two-lines-theme'} underline={false}
                    />
                    <PlanDetails
                        currentPlanData={currentPlanData}
                        handleEditPlan={handleEditPlan}
                    />
                    <div className='plan-details-page-areas-container'>
                        <Banner sectionTittle={t('Areas')} message='' underline={false} headingLevel='h3' />
                        <CustomButton label={t('Add Area')} icon={<AddCircleOutline />} className='plan-card-action-new-area-button white_02'
                            rolesAllowed={['ADMIN', 'EDITOR']} confirm={false}
                            onClick={handleAddNewArea}
                        />
                        <ElementsCarousel
                            elements={currentPlanData.areaDTOs.map((area, index) => {
                                // console.log('AreaMiniCard area:', area);
                                // console.log('AreaMiniCard from:', currentPage);
                                return (
                                    <AreaMiniCard key={area.areaId} area={area} from={currentPage} onDelete={handleDeletedArea} />
                                );
                            })}
                            className={'plan-details-page-areas-carousel'}
                            onElementSelect={handleSelectArea}
                        />
                        {selectedArea && (
                            <div className='plan-details-page-selected-area'>
                                <AreaDetails
                                    selectedArea={selectedArea}
                                />
                                <div className='plan-details-page-area-indicators'>
                                    <Banner sectionTittle={t('Indicators')} message='' underline={false} headingLevel='h2' />
                                    <CustomButton label={t('Add Area Indicator')} icon={<AddCircleOutline />} className='plan-card-action-new-area-indicator-button white_02'
                                        rolesAllowed={['ADMIN', 'EDITOR']} confirm={false}
                                        onClick={handleAddNewAreaIndicator}
                                    />
                                    <B2BElementsExplorer
                                        pagesCount={1}
                                        elementsData={returnArrayAsContentField(selectedArea.areaIndicatorDTOs)}
                                        ElementComponent={AreaIndicatorCard}
                                        elementComponentPropName='areaIndicator'
                                        elementComponentHandlers={{
                                            onDelete: handleDeletedAreaIndicator,
                                            onRefresh: handleRefreshPlanData
                                        }}
                                        className={'plan-details-page-area-indicators-explorer'}
                                        elementIDPropName='areaIndicatorId'
                                        goToID={selectedAreaIndicatorId}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )
    );
};

export default PlanDetailsPage;